/**
 * The Watch's own start and game-over screens: one idea per screen, large
 * text, and tap targets of at least 44 px (the face is only 198×242).
 */
import { useT } from '../../i18n/index.ts';
import { Button } from '../../components/Button.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Wordmark } from '../../components/Wordmark.tsx';
import { formatInteger, formatPercent } from '../model/format.ts';
import { isPersonalBest, shownScores, type FinishedRun } from '../model/gameOver.ts';

export interface WatchStartProps {
  level: number;
  starting: boolean;
  onPlay(): void;
}

export function WatchStart({ level, starting, onPlay }: WatchStartProps) {
  const t = useT();
  return (
    <main className="watch-screen watch-start" aria-label={t('shell.watchStartLabel')}>
      <h1 className="watch-start__title">
        <Wordmark size="compact" />
      </h1>
      <p className="watch-screen__chip">{t('common.levelN', { level })}</p>
      <Button variant="play" icon="play" busy={starting} className="watch-screen__play" onClick={onPlay}>
        {t('shell.startPlay')}
      </Button>
    </main>
  );
}

export interface WatchGameOverProps {
  run: FinishedRun;
  starting: boolean;
  onPlayAgain(): void;
  onHome(): void;
}

export function WatchGameOver({ run, starting, onPlayAgain, onHome }: WatchGameOverProps) {
  const t = useT();
  const scores = shownScores(run);
  const { summary } = run;
  return (
    <main className="watch-screen watch-over" aria-labelledby="watch-over-title">
      <h1 id="watch-over-title" className="watch-over__title">
        {isPersonalBest(run) ? (
          <>
            <Icon name="star" />
            {t('shell.watchBest')}
          </>
        ) : (
          t('shell.watchOver')
        )}
      </h1>
      <p className="watch-over__score">
        <span className="watch-over__value">{formatInteger(scores.rated)}</span>
        <span className="watch-over__label">{t('shell.goRated')}</span>
      </p>
      <p className="watch-over__line">
        {t('common.levelN', { level: summary.levelReached })} · {formatPercent(summary.accuracy)}
      </p>
      <div className="watch-over__actions">
        <Button variant="play" icon="play" busy={starting} className="watch-screen__play" onClick={onPlayAgain}>
          {t('shell.watchAgain')}
        </Button>
        <Button icon="home" className="watch-screen__home" onClick={onHome} aria-label={t('shell.navHome')} />
      </div>
    </main>
  );
}
